import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {catchError, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import * as ProjectActions from './project.actions';
import * as ProcessInfoActions from '../../process-info/services/process-info.actions';
import {ProjectDTO} from '../dto/project';
import {ProjectService} from './project.service';
import {Router} from '@angular/router';

@Injectable()
export class ProjectEffects {
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly actions$: Actions = inject(Actions);
  private readonly router: Router = inject(Router);


  loadProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.loadProjects),
      switchMap(() =>
        this.projectService.getAllProjects().pipe(
          map((projects: ProjectDTO[]) => ProjectActions.loadProjectsSuccess({projects})),
          catchError((error) => of(ProjectActions.loadProjectsFailure({error})))
        )
      )
    )
  );

  loadExploreProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.loadExploreProjects),
      switchMap(() =>
        this.projectService.getAllExploreProjects().pipe(
          map((projects: ProjectDTO[]) => ProjectActions.loadExploreProjectsSuccess({projects})),
          catchError((error) => of(ProjectActions.loadExploreProjectsFailure({error})))
        )
      )
    )
  );

  createProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.createProject),
      mergeMap((action) =>
        this.projectService.createProject(action.project).pipe(
          map((project: ProjectDTO) => ProjectActions.createProjectSuccess({project})),
          catchError((error) => of(ProjectActions.createProjectFailure({error})))
        )
      )
    )
  );

  deleteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.deleteProject),
      mergeMap((action) =>
        this.projectService.deleteProject(action.id).pipe(
          map(() => ProjectActions.deleteProjectSuccess({id: action.id})),
          catchError((error) => of(ProjectActions.deleteProjectFailure({error})))
        )
      )
    )
  );

  retrieveProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.retrieveProject),
      switchMap((action) =>
        this.projectService.getProjectById(action.id).pipe(
          map((project: ProjectDTO) => ProjectActions.retrieveProjectSuccess({project})),
          catchError((error) => of(ProjectActions.retrieveProjectFailure({error})))
        )
      )
    )
  );

  updateProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.updateProject),
      mergeMap((action) =>
        this.projectService.updateProject(action.id, action.project).pipe(
          map((project: ProjectDTO) => ProjectActions.updateProjectSuccess({project})),
          catchError((error) => of(ProjectActions.updateProjectFailure({error})))
        )
      )
    )
  );

  createProjectSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.createProjectSuccess),
      tap(({project}) => this.router.navigate(['/project', project.id])),
      map(() => ProcessInfoActions.restartSseStream())
    )
  );


}
